import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";

const LAST_UPDATED = "16 April 2025";
const CONTACT_EMAIL = "privacy@meetmori.app";

export default function Privacy() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-border/50 bg-background/95 px-5 py-4 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <span className="text-base font-semibold text-foreground">{t("privacy.title")}</span>
      </header>

      <main className="mx-auto max-w-prose px-6 py-10 prose-sm">
        <div className="space-y-8 text-sm text-foreground/90 leading-relaxed">

          <div>
            <p className="text-xs text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            <p className="mt-3">
              Mori (meetmori.app) is a family memory-keeping app built to be private by
              design. This policy explains what data we collect, why we collect it, and how
              we keep it safe. We process data in accordance with the General Data
              Protection Regulation (GDPR) and applicable data protection laws.
            </p>
          </div>

          <Section title="1. Who we are">
            <p>
              Mori is operated by Dodo Solutions s.r.o., a company registered in the
              Slovak Republic. We are the data controller for personal data processed
              through meetmori.app.
            </p>
            <p className="mt-2">
              Contact us at: <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
            </p>
          </Section>

          <Section title="2. What we collect">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>{t("privacy.accountInformation")}</strong> — your email address and the name
                you choose when you sign up.
              </li>
              <li>
                <strong>{t("privacy.voiceRecordings")}</strong> — audio you record inside the app.
                These are stored securely in private cloud storage and are never public.
              </li>
              <li>
                <strong>Photos</strong> — images you upload to memories. Stored in
                private cloud storage.
              </li>
              <li>
                <strong>{t("privacy.memoryContent")}</strong> — stories, titles, categories, dates,
                and any text you write.
              </li>
              <li>
                <strong>Children's information</strong> — first names, dates of birth,
                and gender that you choose to add. We never collect information directly
                from children.
              </li>
              <li>
                <strong>Usage data</strong> — streak counts, memory counts, app settings,
                and language preferences.
              </li>
              <li>
                <strong>{t("privacy.paymentData")}</strong> — processed by Stripe. We do not store
                card numbers or payment credentials.
              </li>
            </ul>
          </Section>

          <Section title="3. How we use your data">
            <ul className="list-disc pl-5 space-y-2">
              <li>To provide and personalise the Mori service.</li>
              <li>
                To generate AI-written memory stories via Google Gemini. Recordings and
                photos are sent to Gemini for this purpose and are not used to train
                Google's models under our API agreement.
              </li>
              <li>To send transactional emails (memory prompts, security alerts).</li>
              <li>To process subscription payments via Stripe.</li>
              <li>To monitor service health and fix errors (Sentry).</li>
            </ul>
            <p className="mt-3">
              We never sell your data. We never use your family memories for advertising.
            </p>
          </Section>

          <Section title="4. Data sharing">
            <p>We share data only with the following processors, bound by data processing agreements:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Supabase</strong> — database and file storage (EU region)</li>
              <li><strong>{t("privacy.googleGeminiApi")}</strong> — AI story generation</li>
              <li><strong>Stripe</strong> — payment processing</li>
              <li><strong>Resend</strong> — transactional email delivery</li>
              <li><strong>Sentry</strong> — error monitoring (no memory content is sent)</li>
            </ul>
          </Section>

          <Section title="5. Data retention">
            <p>
              Your data is retained for as long as your account is active. If you delete
              your account, we delete all personal data within 30 days, except where we
              are required by law to retain it longer.
            </p>
            <p className="mt-2">
              Voice recordings on the free (Seedling) plan expire after 30 days and are
              permanently deleted. Paid plans retain audio for the life of the account.
            </p>
          </Section>

          <Section title="6. Your rights (GDPR)">
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>{t("privacy.rights.access")}</li>
              <li>{t("privacy.rights.correct")}</li>
              <li>{t("privacy.rights.delete")}</li>
              <li>{t("privacy.rights.restrict")}</li>
              <li>Data portability (export your memories)</li>
              <li>{t("privacy.rights.lodge")}</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, email{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
            </p>
          </Section>

          <Section title="7. Security">
            <p>
              All data is encrypted in transit (TLS) and at rest. Voice recordings and
              photos are stored in private buckets — they are never publicly accessible.
              Access within the app uses short-lived signed URLs (1 hour). We use
              row-level security on all database tables to ensure users can only access
              their own data.
            </p>
          </Section>

          <Section title="8. Children's data">
            <p>
              Mori is designed for parents and caregivers. The app is not intended for
              use by children directly. Information about children (names, dates of birth)
              is provided by the parent and is protected under the same privacy rules as
              all other personal data. We do not knowingly collect data directly from
              anyone under 16.
            </p>
          </Section>

          <Section title="9. Cookies">
            <p>
              We use only essential cookies required for authentication and session
              management. We do not use advertising or tracking cookies.
            </p>
          </Section>

          <Section title="10. Changes to this policy">
            <p>
              We may update this policy from time to time. When we make material changes,
              we will notify you by email or in the app. Continued use of Mori after
              changes constitutes acceptance of the updated policy.
            </p>
          </Section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-6 text-center">
        <div className="flex items-center justify-center gap-4">
          <a href="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            meetmori.app
          </a>
          <span className="text-muted-foreground/30 text-xs">·</span>
          <a href="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            Terms of service
          </a>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <div className="text-sm text-foreground/80 leading-relaxed">{children}</div>
    </section>
  );
}
